import "./assets/main.css";
import { createApp, nextTick, watch } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "./router";
import { setupAuth } from "@/auth";
import { registerAuthFailureHandler } from "@/authFailure";
import { useNotificationStore } from "./stores/notification";
import { appPluginsManager } from "@/build-time/contracts/plugin-registry";
import { getSocket, init, warmMangoCaches, serverError } from "luminary-shared";
import {
    appSyncedLanguageIdsAsRef,
    initLanguage,
    initUserActivity,
    isAppLoading,
    isInstalledStandalone,
} from "./globalConfig";
import { apiUrl, deleteExpiredIntervalMs } from "./globalConfig";
import { initAppTitle, initI18n } from "./i18n";
import { initAnalytics } from "./analytics";
import { initSync, initAuthLangSync } from "./sync";
import { initDefaultAffinitySync } from "@/recommendation/defaultAffinityStore";
import { APP_DOCS_INDEX } from "./docsIndex";
import { initSentry, Sentry, sentryDiagnostics } from "@/util/initSentry";
import { markAppReady, markAppError } from "@/util/renderState";
import { notifyUiReady } from "virtual:app-lifecycle";
import { initLivePublishClock } from "@/util/livePublishClock";
import { BOOT_SPLASH_ID } from "./bootSplash";

export const app = createApp(App);

// Install Pinia early so any watchers/effects registered during startup that
// resolve a store (e.g. useNotificationStore) have an active Pinia instance.
app.use(createPinia());

initSentry(app);

/**
 * Content sync window. Installed (standalone) sessions sync the full corpus (no
 * cutoff). Browser-tab sessions sync only the last ~1 month; content with
 * `publishDate` older than `Date.now() - BROWSER_CONTENT_SYNC_WINDOW_MS` is not
 * synced into IndexedDB and is fetched on demand by `HybridQuery`. The tier is
 * decided once per launch — installing takes effect on the next app open.
 */
const BROWSER_CONTENT_SYNC_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // ~1 month

async function Startup() {
    // Pre-warm Mango query caches from localStorage before any queries run.
    // On the first visit this is a no-op; on subsequent loads it eliminates
    // cold-start compilation latency for IndexedDB queries.
    warmMangoCaches();

    // Installed (standalone) users sync the full corpus; browser-tab users get a
    // rolling ~1 month window (older content is fetched on demand by HybridQuery).
    const installedStandalone = isInstalledStandalone();

    await init({
        cms: false,
        docsIndex: APP_DOCS_INDEX,
        apiUrl,
        // The shared "active languages" (keep gate + fallback `$nin`) are the SYNCED subset, not
        // the full preferred display order — so only chosen languages download, while preferred-
        // but-unsynced languages are fetched on demand.
        appLanguageIdsAsRef: appSyncedLanguageIdsAsRef,
        contentPublishDateCutoff: installedStandalone
            ? undefined // no cutoff → full corpus
            : Date.now() - BROWSER_CONTENT_SYNC_WINDOW_MS,
        diagnostics: sentryDiagnostics,
        deleteExpiredIntervalMs,
    });

    // Keep the CMS-managed default-affinity baseline/config in sync with the local
    // copy of the singleton doc, now that it's synced like any other doc type.
    initDefaultAffinitySync();

    const socket = getSocket();

    // Advance the session "now" bound when live-sync delivers newly published
    // content, so the Newest feed (and every content feed) picks it up live
    // without a page refresh. Registered before connect — socket.io buffers it.
    initLivePublishClock();

    registerAuthFailureHandler();

    // Start the auth-provider/language sync watcher before setupAuth(), which may
    // connect the socket (directly, or indirectly via the auth-failure handler
    // above forcing an anonymous reconnect): if the watcher isn't listening yet,
    // the isConnected/accessMap transition that should kick off the AuthProvider
    // sync is missed until some later, unrelated change re-triggers it.
    initAuthLangSync();

    // Provide the build-target services before setupAuth(), which resolves the
    // platform auth-flow strategy from the app.
    app.use(appPluginsManager);

    await setupAuth(app, router);
    socket.connect(); // ensure socket connects for public users (no-op if auth already called reconnect())

    // Install all plugins before mounting — components rendered during the
    // splash screen (e.g. SearchModal) call useI18n() at setup time.
    const i18n = initI18n();

    // Show notification on server error (5xx), debounced to avoid flooding.
    // Translation is owned by the client, not the shared lib.
    let serverErrorTimeout: ReturnType<typeof setTimeout> | null = null;
    watch(serverError, (error) => {
        if (error) {
            serverError.value = null;
            console.error(
                `Server error: ${error.status}${error.message ? ` ${error.message}` : ""}`,
            );
            if (serverErrorTimeout) return;
            Sentry?.captureMessage(
                `Server error: ${error.status}${error.message ? ` ${error.message}` : ""}`,
                "error",
            );
            const { t } = i18n.global;
            useNotificationStore().addNotification({
                title: t("notification.server_error.title"),
                description: t("notification.server_error.description"),
                state: "error",
                type: "toast",
                timeout: 10000,
            });
            // Debounce server error notifications to avoid flooding if multiple requests fail in quick succession
            serverErrorTimeout = setTimeout(() => {
                serverErrorTimeout = null;
            }, 5000);
        }
    });

    app.use(router);
    app.use(i18n);
    app.mount("#app");
    // The web loading UI is rendering from here on — the build target may have
    // launch chrome of its own to hand over from.
    notifyUiReady();

    await initLanguage();
    initSync();

    initUserActivity();

    isAppLoading.value = false;

    // Drop the boot splash only after the flag above has been rendered, so the app is already
    // painted underneath and uncovering it can't expose a blank frame. A failed startup never
    // reaches this line, which is deliberate: markAppError() below leaves the splash up and
    // switches it to its error panel rather than stranding the user on an animating bar.
    await nextTick();
    document.getElementById(BOOT_SPLASH_ID)?.remove();

    initAppTitle(i18n);
    initAnalytics();
    markAppReady();
}

Startup().catch((err) => {
    console.error(err);
    Sentry?.captureException(err);
    markAppError();
    // The error state is UI too — don't strand any launch chrome over it.
    notifyUiReady();
});
